/**
 * Eine Karte auf der Seite „Verbindungen“: Titel, Zustand, Stichworte — wahlweise zum Aufklappen. (22.10.2026)
 *
 * Martin, 22.10.2026: *„Da geht Panel ganz unter. Das sollte eigentlich auch in so einem schönen Feld sein wie
 * Whoop und das andere, damit es die gleiche Darstellung hat, nur dass man dann halt Panel aufklappen kann.“*
 *
 * Bis hierher baute jede Karte (Google, WHOOP, Claude CLI) ihren Kopf selbst, Zeile für Zeile gleich, und das Panel
 * stand als schlichte Zeile in einem `einklappbar(...)` darüber. Jetzt baut EIN Baustein den Kopf; die Gestalt steht
 * in style.css (`.connector-*`, `.verbindungs-karte-*`).
 *
 * Aufklappbar: der ganze Kopf (Titel, Zustand, Stichworte, Pfeil) ist EIN `<button>` mit `aria-expanded` und
 * `aria-controls` — mindestens 44 px hoch, Tastatur und Antippen gleich. Zu ist der Standard; mit `merken` merkt sich
 * dieses Gerät, ob die Karte offen war.
 *
 * ⚠️ Kein `<details>`: der Inhalt des Panels hat selbst ein `<details>` (Schlüssel von Hand, nur Betrieb), und seine
 *    Wächter fragen nach genau diesem — ein zweites hätte sie getäuscht.
 * ⚠️ Im Knopf stehen nur `<span>`s: ein `<div>` ist in einem `<button>` nicht erlaubt.
 */

export interface VerbindungsKarte {
  karte: HTMLElement;
  titel: HTMLElement;
  status: HTMLElement;
  unter: HTMLElement;
  /** Nur bei `aufklappbar`: der Inhalt unter dem Kopf; sonst null. */
  koerper: HTMLElement | null;
  /** Nur bei `aufklappbar`: auf- oder zuklappen (z. B. wenn die Karte etwas zu sagen hat). */
  setzeOffen: (offen: boolean) => void;
}

export interface VerbindungsKartenOptionen {
  titel: string;
  unter: string;
  /** Der Text der Marke rechts oben, bis der Stand da ist. */
  status?: string;
  /** Weitere Klassen der Karte (z. B. „panel-karte“). */
  klasse?: string;
  aufklappbar?: boolean;
  /** Schlüssel, unter dem dieses Gerät „offen“ merkt (nur mit `aufklappbar`). */
  merken?: string;
}

let zaehler = 0;
const MERK_VORSILBE = "vespera.verbindung-offen.";

function liesOffen(merken: string | undefined): boolean {
  if (!merken) return false;
  try {
    return localStorage.getItem(MERK_VORSILBE + merken) === "1";
  } catch {
    return false;
  }
}

function merkeOffen(merken: string | undefined, offen: boolean): void {
  if (!merken) return;
  try {
    if (offen) localStorage.setItem(MERK_VORSILBE + merken, "1");
    else localStorage.removeItem(MERK_VORSILBE + merken);
  } catch {
    /* privates Fenster, gesperrter Speicher: dann eben jedes Mal zu */
  }
}

function span(klasse: string, text?: string): HTMLSpanElement {
  const s = document.createElement("span");
  s.className = klasse;
  if (text !== undefined) s.textContent = text;
  return s;
}

export function verbindungsKarte(o: VerbindungsKartenOptionen): VerbindungsKarte {
  const nr = ++zaehler;
  const karte = document.createElement("div");
  karte.className = o.klasse ? `connector-card ${o.klasse}` : "connector-card";

  const titel = span("connector-title", o.titel);
  titel.id = `verbindungs-karte-titel-${nr}`;
  const status = span("connector-status", o.status ?? "wird geprüft…");
  const unter = span("connector-sub", o.unter);

  if (!o.aufklappbar) {
    const kopf = document.createElement("div");
    kopf.className = "connector-card-head";
    kopf.append(titel, status);
    karte.append(kopf, unter);
    return { karte, titel, status, unter, koerper: null, setzeOffen: () => undefined };
  }

  karte.setAttribute("role", "group");
  karte.setAttribute("aria-labelledby", titel.id);

  const knopf = document.createElement("button");
  knopf.type = "button";
  knopf.className = "verbindungs-karte-kopf";
  const zeile = span("connector-card-head");
  zeile.append(titel, status, span("verbindungs-karte-pfeil"));
  zeile.lastElementChild?.setAttribute("aria-hidden", "true");
  knopf.append(zeile, unter);

  const koerper = document.createElement("div");
  koerper.className = "verbindungs-karte-koerper";
  koerper.id = `verbindungs-karte-koerper-${nr}`;
  knopf.setAttribute("aria-controls", koerper.id);

  const setzeOffen = (offen: boolean): void => {
    knopf.setAttribute("aria-expanded", String(offen));
    koerper.hidden = !offen;
  };
  setzeOffen(liesOffen(o.merken));
  knopf.addEventListener("click", () => {
    const offen = knopf.getAttribute("aria-expanded") !== "true";
    setzeOffen(offen);
    merkeOffen(o.merken, offen);
  });

  karte.append(knopf, koerper);
  return { karte, titel, status, unter, koerper, setzeOffen };
}
